import type { Escala, Pregunta, TipoPregunta } from '@copathy/shared';

/**
 * Una pregunta **tal como la verá quien la responda**, en un teléfono.
 *
 * ── Dónde se responde de verdad ──
 * En la app móvil (D-011), o en la web responsive en una pantalla angosta. De ahí
 * salen las dos reglas duras de este archivo:
 *
 * 1. **Cero scroll horizontal.** La matriz NO es una tabla: es cada afirmación
 *    con su escala debajo, apilada. Una tabla de siete columnas en 375 px o
 *    scrollea de lado o se vuelve ilegible — las dos cosas que un formulario
 *    cómodo no puede hacer. Jonathan lo señaló mirando la previa.
 * 2. **Área de toque generosa.** Cada opción es una pastilla de alto completo,
 *    no un circulito de 16 px al que hay que atinarle con el pulgar.
 *
 * ── El acento de sección ──
 * `acento` llega de la sección a la que pertenece la pregunta y tiñe el número,
 * los bordes activos y los controles. Es lo que rompe la monotonía de un
 * formulario largo sin convertirlo en un arcoíris: **una** familia de color por
 * página.
 *
 * ── Qué se reutiliza y qué no ──
 * ⚠️ Este archivo **no cruza a React Native** (CLAUDE.md): lo que cruza es la
 * forma, en `shared/`. Dentro de la web sí es la misma pieza que usará la
 * pantalla de responder (M7): hoy inerte, y cuando llegue el momento entran
 * `valor` y `alResponder` sin cambiarle la forma.
 *
 * ⚠️ **`inert` y no solo `pointer-events-none`**: un formulario invisible al
 * ratón sigue en el orden de tabulación.
 */

export type Acento = {
  /** Color pleno: números, bordes seleccionados. */
  fuerte: string;
  /** Fondo suave: chips, resaltados. */
  suave: string;
};

/**
 * La paleta, del nombre guardado al tono de la web. El nombre viaja por la API;
 * el tono lo decide cada pantalla — la app móvil tendrá el suyo.
 */
export const ACENTOS: Record<string, Acento> = {
  marino: { fuerte: '#1c4270', suave: '#eef3f9' },
  naranja: { fuerte: '#e25703', suave: '#fff3ea' },
  verde: { fuerte: '#0e7a4e', suave: '#ecf8f1' },
  morado: { fuerte: '#6d28d9', suave: '#f4effd' },
  turquesa: { fuerte: '#0d7490', suave: '#ebf7fa' },
  rosa: { fuerte: '#be1862', suave: '#fdeef5' },
};

export const ACENTO_BASE: Acento = ACENTOS.marino!;

export function PreguntaRespondible({
  pregunta,
  escala,
  numero,
  acento = ACENTO_BASE,
  resaltada,
}: {
  pregunta: Pregunta;
  escala: Escala | undefined;
  numero: number;
  acento?: Acento;
  /** La que se está editando ahora mismo en el constructor. */
  resaltada?: boolean;
}) {
  return (
    <article
      inert
      className="rounded-2xl border bg-white p-4 shadow-[0_1px_2px_rgba(15,36,64,0.06)] transition"
      style={{
        borderColor: resaltada ? acento.fuerte : '#e4ebf3',
        boxShadow: resaltada ? `0 0 0 4px ${acento.suave}` : undefined,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-sm font-bold text-white"
          style={{ background: acento.fuerte }}
        >
          {numero}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-snug text-marino-900">
            {pregunta.emoji && (
              <span className="mr-1.5" aria-hidden="true">
                {pregunta.emoji}
              </span>
            )}
            {pregunta.texto || <span className="font-normal text-marino-300">Sin enunciado todavía</span>}
            {pregunta.obligatoria && (
              <span className="ml-1" style={{ color: acento.fuerte }}>
                *
              </span>
            )}
          </p>
          {pregunta.ayuda && <p className="mt-1 text-sm text-marino-400">{pregunta.ayuda}</p>}
        </div>
      </div>

      <div className="mt-3.5">
        <Control pregunta={pregunta} escala={escala} acento={acento} />
      </div>
    </article>
  );
}

function Control({
  pregunta,
  escala,
  acento,
}: {
  pregunta: Pregunta;
  escala: Escala | undefined;
  acento: Acento;
}) {
  const tipo: TipoPregunta = pregunta.tipo;

  if (tipo === 'likert') {
    if (!escala) return <Pendiente>Elige una escala para ver cómo queda.</Pendiente>;
    return <FilaEscala escala={escala} acento={acento} />;
  }

  if (tipo === 'matriz_likert') {
    if (!escala) return <Pendiente>Elige una escala para ver cómo queda.</Pendiente>;
    if (pregunta.items.length === 0) {
      return <Pendiente>Agrega las afirmaciones que van en la matriz.</Pendiente>;
    }
    /*
      ⚠️ NADA de tabla. En un teléfono, la matriz es cada afirmación con su
      escala debajo, apilada. La misma escala se repite —eso es lo que la hace
      matriz—, pero el pulgar nunca tiene que salir del ancho de la pantalla.
    */
    return (
      <div className="space-y-3.5">
        {pregunta.items.map((item, i) => (
          <div
            key={item.id}
            className={i > 0 ? 'border-t border-dashed border-marino-100 pt-3.5' : ''}
          >
            <p className="mb-2 text-sm font-medium text-marino-700">{item.texto}</p>
            <FilaEscala escala={escala} acento={acento} compacta />
          </div>
        ))}
      </div>
    );
  }

  if (tipo === 'opcion_unica' || tipo === 'opcion_multiple' || tipo === 'si_no') {
    /*
      `si_no` no guarda ítems: sus dos opciones son fijas. Teclear «Sí» y «No» en
      cada pregunta es fricción, y guardarlos permitiría un sí/no cuyas opciones
      dijeran otra cosa.
    */
    const opciones =
      tipo === 'si_no'
        ? [{ id: -1, texto: 'Sí' }, { id: -2, texto: 'No' }]
        : pregunta.items.map((i) => ({ id: i.id, texto: i.texto }));

    if (opciones.length === 0) return <Pendiente>Agrega al menos dos opciones.</Pendiente>;

    const multiple = tipo === 'opcion_multiple';
    return (
      <>
        <ul className="space-y-2">
          {opciones.map((o) => (
            <li
              key={o.id}
              className="flex items-center gap-3 rounded-xl border border-marino-100 bg-white px-3.5 py-2.5 text-sm text-marino-800"
            >
              <span
                className={`inline-block h-[18px] w-[18px] shrink-0 border-2 bg-white ${
                  multiple ? 'rounded-[5px]' : 'rounded-full'
                }`}
                style={{ borderColor: acento.fuerte }}
              />
              {o.texto}
            </li>
          ))}
        </ul>
        {multiple && (pregunta.minimo !== null || pregunta.maximo !== null) && (
          <p className="mt-2 text-xs text-marino-400">
            {pregunta.minimo !== null && pregunta.maximo !== null
              ? `Elige entre ${pregunta.minimo} y ${pregunta.maximo}.`
              : pregunta.maximo !== null
                ? `Elige hasta ${pregunta.maximo}.`
                : `Elige al menos ${pregunta.minimo}.`}
          </p>
        )}
      </>
    );
  }

  if (tipo === 'texto_corto') return <CajaFalsa alto="h-11">Escribe tu respuesta…</CajaFalsa>;
  if (tipo === 'texto_largo') return <CajaFalsa alto="h-24">Escribe tu respuesta…</CajaFalsa>;

  if (tipo === 'numero') {
    const rango =
      pregunta.minimo !== null && pregunta.maximo !== null
        ? `Entre ${pregunta.minimo} y ${pregunta.maximo}`
        : pregunta.maximo !== null
          ? `Hasta ${pregunta.maximo}`
          : pregunta.minimo !== null
            ? `Desde ${pregunta.minimo}`
            : 'Un número';
    return (
      <CajaFalsa alto="h-11" ancho="max-w-40">
        {rango}
      </CajaFalsa>
    );
  }

  if (tipo === 'fecha') {
    return (
      <CajaFalsa alto="h-11" ancho="max-w-44">
        dd / mm / aaaa
      </CajaFalsa>
    );
  }

  // Las secciones no llegan acá: el renderizador de páginas las convierte en
  // cabeceras antes de repartir preguntas.
  return null;
}

/**
 * La escala como fila de pastillas numeradas con las etiquetas de los extremos
 * debajo — el patrón con el que una Likert de 5 (o de 11) cabe en 350 px sin
 * scrollear. Escribir las cinco etiquetas completas como botones apilados haría
 * de cada matriz una pared de texto repetido.
 */
function FilaEscala({
  escala,
  acento,
  compacta,
}: {
  escala: Escala;
  acento: Acento;
  compacta?: boolean;
}) {
  const primera = escala.puntos[0];
  const ultima = escala.puntos[escala.puntos.length - 1];
  return (
    <div>
      <div className="flex gap-1.5">
        {escala.puntos.map((p) =>
          p.emoji ? (
            /*
              La carita va SOLA, sin caja ni borde — Jonathan lo pidió así: el
              emoji ya es la figura, y encerrarlo en un botón lo vuelve un icono
              más. El estado «elegida» (M7) se marcará con escala y opacidad, no
              con un marco.
            */
            <span
              key={p.id}
              title={p.etiqueta}
              className={`grid flex-1 place-items-center leading-none ${
                compacta ? 'h-10 text-2xl' : 'h-12 text-3xl'
              }`}
            >
              {p.emoji}
            </span>
          ) : (
            <span
              key={p.id}
              title={p.etiqueta}
              className={`grid flex-1 place-items-center rounded-xl border font-semibold text-marino-600 ${
                compacta ? 'h-10 text-xs' : 'h-12 text-sm'
              }`}
              style={{ borderColor: '#dbe5f0', background: acento.suave }}
            >
              {p.orden}
            </span>
          ),
        )}
      </div>
      {primera && ultima && (
        <div className="mt-1.5 flex justify-between text-[11px] leading-tight text-marino-400">
          <span className="max-w-[45%]">{primera.etiqueta}</span>
          <span className="max-w-[45%] text-right">{ultima.etiqueta}</span>
        </div>
      )}
    </div>
  );
}

/** Lo que falta para que la pregunta esté completa. Se ve en la vista previa. */
function Pendiente({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed border-marino-200 bg-marino-50/40 px-3.5 py-3 text-sm text-marino-400">
      {children}
    </p>
  );
}

/** Un campo dibujado, no un `<input>` deshabilitado: acá no se escribe nada. */
function CajaFalsa({
  alto,
  ancho = '',
  children,
}: {
  alto: string;
  ancho?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-start rounded-xl border border-marino-200 bg-white px-3.5 py-3 text-sm
        text-marino-300 ${alto} ${ancho}`}
    >
      {children}
    </div>
  );
}
